import { useContext } from 'react';
import {
    Box, Button, Dialog, DialogActions,
    DialogContent, DialogTitle, TextField, FormControl,
    InputLabel, Select, MenuItem, Divider
} from '@mui/material';
import useLocalStorage from '../../../Hooks/useLocalStorage';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import UserDialogBoxEdit from "./UserDialogBoxEdit";
import useAdminControlsStore from "../../../Stores/AdminControlsStore";
import API from "../../../api2";
import useNotification from "../../../Hooks/useNotification";
import CustomerLocationSelect from "../CustomerLocationSelect";
import UserContext from '../../UserSettings/UserContext';
import { User } from '../../../Types/User';

interface UserDialogBoxProps {
    dialogMode: 'create' | 'edit';
    openDialog: boolean;
    handleCloseDialog: () => void;
}

function UserDialogBox({ dialogMode, openDialog, handleCloseDialog }: UserDialogBoxProps) {
    const { user, setUser } = useAdminControlsStore();
    const userContextData = useContext(UserContext);
    const userContext = userContextData?.user;
    const [userGlobal, setUserGlobal] = useLocalStorage<User>("user");
    const notify = useNotification();

    const handleSubmit = async () => {
        let result;
        if (dialogMode === 'create') result = await API.createUser(user);
        else if (dialogMode === 'edit') result = await API.editUser(user);
        if (userContext?.id === user.id) {
            const updatedUser = {
                ...userGlobal,
                id: user.id,
                email: user.email,
                accountLevel: user.accountLevel,
                customers: user.customers,
                locations: user.locations,
                active: user.active,
                resetPassword: user.resetPassword,
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                emailVerified: user.emailVerified || false,
            };
            
            // Update localStorage
            setUserGlobal(updatedUser);        
            if (userContextData?.setUser) {
                userContextData.setUser(updatedUser);
            }
        }
        notify(result.message, result.error ? 'error' : 'success');
        if (result.error) return;
        handleCloseDialog();
    };

    return (<>
        {/* Dialog for creating/editing users */}
        < Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth >
            <DialogTitle>
                {dialogMode === 'create' ? 'Create New User' : 'Edit User'}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
                <Box component="form" sx={{ '& .MuiTextField-root': { my: 1 } }}>
                    <TextField
                        fullWidth
                        required
                        label="First Name"
                        name="firstName"
                        value={user.firstName}
                        onChange={(e) => setUser(e.target.name, e.target.value)}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        required
                        label="Last Name"
                        name="lastName"
                        value={user.lastName}
                        onChange={(e) => setUser(e.target.name, e.target.value)}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        required
                        type="email"
                        label="Email"
                        name="email"
                        value={user.email}
                        onChange={(e) => setUser(e.target.name, e.target.value)}
                        margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="account-level-label">Account Level</InputLabel>
                        <Select
                            labelId="account-level-label"
                            name="accountLevel"
                            value={user.accountLevel || "User"}
                            label="Account Level"
                            onChange={(e) => setUser(e.target.name, e.target.value)}
                        >
                            <MenuItem value="User">User</MenuItem>
                            <MenuItem value="Admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                    {dialogMode === 'edit' && (
                        <UserDialogBoxEdit />
                    )}
                </Box>
                <CustomerLocationSelect
                    edit={dialogMode === 'edit' ? true : false}
                    editData={dialogMode === 'edit' ? user : {}}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={dialogMode === 'create' ? <AddIcon /> : <RefreshIcon />}
                >
                    {dialogMode === 'create' ? 'Create' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog >
    </>);
}

export default UserDialogBox;